import React, { memo, useState, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import WarningPanel from 'components/WarningPanel';
import DashboardContainer from 'components/DashboardContainer';
import FundraiserThumbnail from 'components/FundraiserThumbnail';
import Pagination from 'components/Pagination';
import CardWithAction from 'components/CardWithAction';
import Loader from 'components/Loader';

import { fundraiserList as TEXT } from 'locales/en';
import PATHS from 'routes/paths';
import {
  getFundraiserList,
  fundraiserSelectors,
  editFundraiser,
} from 'redux/slices/fundraiser';
import { currentCompanySelector } from 'redux/slices/company';
import { logEvent, logActionRequired, logClickFundraiser } from 'utils/ga';

const FundraiserList = () => {
  const [pageChange, setPageChange] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const fundraisers = useSelector(fundraiserSelectors.selectAll);
  const fundraiserCount = useSelector(fundraiserSelectors.selectTotal);
  const currentCompany = useSelector(currentCompanySelector);
  const nodeRef = useRef(null);

  const pagination = useSelector((state) => state.fundraiser.pagination);
  const loading = useSelector((state) => state.fundraiser.loading);
  const companyLoading = useSelector((state) => state.company.loading);
  const storeLoading = useSelector((state) => state.javelinStore.loading);
  const displayFullPageLoader =
    storeLoading || companyLoading || (companyLoading && loading);

  const displayPanel = useMemo(() => {
    if (!currentCompany.account_holder_verifications?.length) {
      return false;
    }
    return currentCompany.account_holder_verifications.some(
      (item) =>
        ['FAILED', 'INVALID_DATA', 'AWAITING_DATA'].indexOf(item.status) > -1
    );
  }, [currentCompany.account_holder_verifications]);

  const renderView = useCallback(() => {
    const handleChangeOnSale = (productId, value) => {
      dispatch(editFundraiser({ id: productId, on_sale: value }));
    };

    const onPageChange = async ({ selected }) => {
      window.scrollTo(0, 0);
      setPageChange(true);
      dispatch(getFundraiserList({ page: selected + 1 })).then(() =>
        setPageChange(false)
      );
    };

    if (pageChange) {
      return <Loader />;
    } else {
      return (
        <>
          <CSSTransition
            classNames="transitionGroup"
            in
            appear
            timeout={300}
            key={fundraisers[0].id}
            nodeRef={nodeRef}
          >
            <div
              ref={nodeRef}
              className="grid w-full auto-rows-auto auto-cols-auto items-start gap-y-5 my-9"
            >
              {fundraisers.map((product) => {
                return (
                  <FundraiserThumbnail
                    key={product.id}
                    product={product}
                    onChangeOnSale={() => {
                      logEvent('click', {
                        category: 'live_flag_fundraiser',
                        label: product.on_sale
                          ? 'fundraiser_inactive'
                          : 'fundraiser_active',
                        is_live: product.on_sale ? '0' : '1',
                        product_id: product.id,
                      });
                      handleChangeOnSale(product.id, !product.on_sale);
                    }}
                  />
                );
              })}
            </div>
          </CSSTransition>
          <Pagination
            totalPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            onPageChange={onPageChange}
          />
        </>
      );
    }
  }, [
    dispatch,
    fundraisers,
    pageChange,
    pagination.currentPage,
    pagination.totalPages,
  ]);

  return (
    <DashboardContainer
      header={fundraiserCount ? TEXT.header1 : TEXT.emptyHeader1}
      subheader={fundraiserCount ? TEXT.header2 : TEXT.emptyHeader2}
      fullPageLoading={displayFullPageLoader}
      transitionIn={!displayFullPageLoader}
      transitionAppear={displayFullPageLoader}
    >
      {displayPanel && (
        <WarningPanel
          testId="error-panel"
          onClick={() => {
            logActionRequired();
            history.push(PATHS.home);
          }}
          text={TEXT.panelHeader}
          text2={TEXT.panelText}
        />
      )}
      <CardWithAction
        title={!fundraiserCount ? TEXT.createFirst : TEXT.create}
        subtitle={TEXT.createFirstSubtitle}
        cta={TEXT.create}
        to={PATHS.fundraisersNew}
        callback={() => logClickFundraiser(fundraiserCount)}
        className="flex flex-col lg:flex-row lg:h-40 items-center text-center lg:text-left lg:mb-0"
        testId="fundraiser-new"
      />
      {!!fundraiserCount && renderView()}
    </DashboardContainer>
  );
};

export default memo(FundraiserList);
